import { useEffect, useRef } from 'react'
import type { DocumentDef } from '@project/contracts'
import { useApi } from '../app/api.js'
import type { DocumentForm } from './FormTab.js'

const UNSET = Symbol('unset')

const empty = (v: unknown): boolean => v === undefined || v === null || v === ''

/**
 * Reference: choosing the Customer on a project fills the Billing Address and
 * Site Address with the customer's address and the Consignee with its name,
 * which the user then adjusts. Only what is still empty is filled, and only
 * when the customer actually changes — opening a saved project changes nothing.
 */
export const useProjectCustomerDefaults = (def: DocumentDef, form: DocumentForm, readOnly: boolean): void => {
  const api = useApi()
  const customerId = form.watch('customerId')
  const previous = useRef<unknown>(UNSET)
  const enabled = def.key === 'project' && !readOnly

  useEffect(() => {
    const before = previous.current
    previous.current = customerId
    // First render: whatever the saved project already holds is not a change.
    if (!enabled || before === UNSET || (before ?? '') === (customerId ?? '')) return
    if (typeof customerId !== 'string' || customerId === '') return
    let live = true
    api.projectCustomerDefaults(customerId)
      .then((d) => {
        if (!live) return
        const fill = (key: string, value: string | null) => {
          if (value && empty(form.getValues(key))) form.setValue(key, value, { shouldDirty: true })
        }
        fill('consignee', d.consignee)
        fill('billingAddressId', d.billingAddressId)
        fill('siteAddressId', d.siteAddressId)
        fill('billingContactId', d.billingContactId)
      })
      .catch(() => undefined)
    return () => { live = false }
  }, [api, customerId, enabled, form])
}
